"use client";

import AppDownloadButtons from "@/components/app-download-buttons";

// import Component from "@/components/comp-531";
import Faq from "@/components/faq";
import Footer from "@/components/footer";
import HeroImage from "@/components/hero-image";
import Navbar from "@/components/navbar";
import PricingSection from "@/components/pricing-section";
import Separator from "@/components/separator";
import TabsSection from "@/components/tabs-section";
import Testimonials from "@/components/testimonials";
import { cn } from "@/lib/utils";
import { useUserStore } from "@/store/store";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/routing";
import { isAuthenticated } from "@/service/auth-service";
import { useEffect, useState } from "react";
import { ClientOnlyDialogWrapper } from "@/components/client-only-dialog-wrapper";

export default function Home() {
  const locale = useLocale();
  const t = useTranslations("home");
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { role, isAuthenticated: isUserAuthenticated, setIsAuthenticated } = useUserStore();
  const isArabic = locale === "ar";
  const slashClassName = cn(
    "pointer-events-none absolute inset-x-[6%] block h-[0.17em] rounded-full",
    "bg-gradient-to-l from-[#8100FF] to-[#323DFB] opacity-70 transform",
    isArabic ? "rotate-[-8deg]" : "rotate-[-14deg]",
    isArabic
      ? "top-[82%] sm:top-[78%] md:top-[74%] lg:top-[70%] xl:top-[66%]"
      : "top-[22%] sm:top-[20%] md:top-[18%] lg:top-[16%] xl:top-[22%]",
    isArabic ? "origin-top-left" : "origin-top-right"
  );

  // Mark component as mounted to prevent hydration mismatch
  // Use a delay to ensure React has finished initial hydration
  useEffect(() => {
    // Only set mounted after we're sure we're on the client
    if (typeof window !== "undefined") {
      // Use a small delay to ensure hydration is complete
      const timer = setTimeout(() => {
        setMounted(true);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, []);

  // Sync user store with token on page load
  // This ensures that if a token exists but the store hasn't hydrated yet,
  // we sync the authentication state
  useEffect(() => {
    if (typeof window === "undefined") return;
    
    const hasToken = isAuthenticated();
    // If there's a token but store shows not authenticated, sync it
    // The persist middleware should restore user data, but we ensure isAuthenticated is synced
    if (hasToken && !isUserAuthenticated) {
      // If user data exists in store, ensure isAuthenticated is true
      // If user data doesn't exist but token does, the store should have it from persist
      // Wait a bit for persist to hydrate, then sync
      const timer = setTimeout(() => {
        if (isAuthenticated() && !isUserAuthenticated) {
          setIsAuthenticated(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUserAuthenticated, setIsAuthenticated]);

  // Allow both trainers and clients to access the storefront
  // Users can navigate to their dashboard manually if needed
  // Note: Trainers will be redirected to wizard when trying to access /app routes if profile is incomplete

  useEffect(() => {
    // Only run after component is mounted to prevent hydration issues
    if (!mounted) return;
    
    // This effect runs only once when the component mounts
    // Skip role selection dialog if user is authenticated
    if (typeof window !== "undefined" && isAuthenticated()) {
      return;
    }
    
    try {
      const storedRole = localStorage.getItem("user-role-storage");
      if (!storedRole) {
        // Use setTimeout to ensure this happens after React has finished hydration
        setTimeout(() => setOpen(true), 0);
        return;
      }
      
      const parsedRole = JSON.parse(storedRole);
      if (!parsedRole?.state?.role) {
        setTimeout(() => setOpen(true), 0);
        return;
      }
      
      // If we have a stored role, it will be set by the store's persist middleware
      // No need to set it here as it's already persisted
    } catch (error) {
      console.error("Error parsing stored role:", error);
      setTimeout(() => setOpen(true), 0);
    }
  }, [mounted]);

  return (
    <>
      {/* Use client-only wrapper to ensure dialog never renders during SSR */}
      {/* Only render wrapper after mount to prevent any SSR rendering */}
      {mounted && (
        <ClientOnlyDialogWrapper open={open} onOpenChange={setOpen} />
      )}
      <Navbar />
      <main className="min-h-screen bg-white overflow-hidden">
        <section
          className="relative h-[calc(100vh-3.64rem)] mt-[3.64rem] lg:h-[calc(100vh-4.5rem)] lg:mt-[4.5rem] w-full"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <HeroImage />
           <div
             className={cn(
              "z-20 flex flex-col justify-center h-full gap-6 w-full px-4 lg:px-16",
              isArabic
                ? "items-center lg:items-end text-center lg:text-right"
                : "items-center lg:items-start text-center lg:text-left"
             )}
           >
             <div
              className="z-20 flex flex-col items-center lg:items-start gap-6"
             >
              <h2
                className="text-5xl sm:text-6xl md:text-7xl xl:text-8xl xl:leading-tight z-10 text-primary-foreground"
              >
                {role === "trainer" ? t("title-1") : t("title-client-1")}{" "}
              </h2>
              <h2
                className="text-5xl sm:text-6xl md:text-7xl xl:text-8xl xl:leading-tight z-10 text-primary-foreground"
              >
                {role === "trainer" ? t("title-2") : t("title-client-2")}{" "}
              </h2>
              <h2
                className="relative w-fit text-5xl sm:text-6xl md:text-7xl xl:text-8xl xl:leading-tight z-10 text-primary-foreground"
              >
                {locale === "en" ? (
                  <>
                                         {role === "trainer" ? "Achieve your " : "Achieve your "}
                     <span className="relative inline-block">
                       {role === "trainer" ? "goals" : "goals"}
                      <span aria-hidden="true" className={slashClassName}></span>
                     </span>
                  </>
                ) : (
                  <>
                                         {role === "trainer" ? "حقق " : "حقق "}
                     <span className="relative inline-block">
                       {role === "trainer" ? "أهدافك" : "أهدافك"}
                      <span aria-hidden="true" className={slashClassName}></span>
                     </span>
                  </>
                )}
              </h2>
            </div>
            <div
              className={cn(
                "z-30 w-full flex justify-center",
                isArabic ? "mt-8 lg:justify-end" : "mt-4 lg:justify-start"
              )}
            >
              <AppDownloadButtons
                buttonWidthClass="w-40 sm:w-48"
                stackOnMobile={false}
                className={cn(
                  "items-center",
                  isArabic ? "sm:flex-row-reverse" : undefined
                )}
              />
            </div>
          </div>
        </section>
        <TabsSection />
        <Separator />
        <Testimonials />
        {/* <AltTimeline /> */}
        {/* <AnimatedTabsSection /> */}
        {/* <Component /> */}
        {/* <Subscribe /> */}
        {role === "trainer" && <PricingSection />}
      </main>
      <Footer />
    </>
  );
}
